import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Collapse, IconButton } from '@mui/material';
import { useEffect, useState } from 'react';
import { 
  fogForecastService, 
  FogForecastResponse, 
  AggregatedPredictions, 
  AggregatedActuals,
  FogActual,
  FogPrediction
} from '../services/FogForecastService';
import { NZAirports } from '../types/Locations';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// Airport code to full name mapping
const AIRPORT_NAMES: Record<string, string> = {
  "NZCH": "Christchurch Airport",
  "NZHN": "Hamilton Airport",
  "NZAA": "Auckland Airport",
  "NZWN": "Wellington Airport",
  "NZDN": "Dunedin Airport",
  "NZQN": "Queenstown Airport",
  "NZNS": "Nelson Airport",
  "NZNP": "New Plymouth Airport",
  "NZPM": "Palmerston North Airport",
  "NZTG": "Tauranga Airport",
  "NZRO": "Rotorua Airport",
  "NZGS": "Gisborne Airport",
  "NZNR": "Napier Airport",
  "NZCI": "Chatham Islands Airport",
  "NZHK": "Hokitika Airport",
  "NZWF": "Wanaka Airport",
  "NZWS": "Westport Airport",
  "NZWK": "Whakatane Airport",
  "NZAP": "Taupo Airport",
  "NZKT": "Kaitaia Airport",
  "NZKK": "Kerikeri Airport"
};

// Get the first 5 locations from NZAirports enum
const TARGET_AIRPORTS = [
  NZAirports.NZAA, // Auckland
  NZAirports.NZCH, // Christchurch
  NZAirports.NZDN, // Dunedin
  NZAirports.NZHN, // Hamilton
  NZAirports.NZWN  // Wellington
];

interface LocationForecast {
  locationId: NZAirports;
  locationName: string;
  forecast: FogForecastResponse | null;
  error: string | null;
}

interface RowProps {
  data: AggregatedActuals | AggregatedPredictions;
  type: 'actual' | 'prediction';
}

const DataRow: React.FC<RowProps> = ({ data, type }) => {
  const [open, setOpen] = useState(false);
  const isActual = type === 'actual';
  const actualData = isActual ? data as AggregatedActuals : null;
  const predictionData = !isActual ? data as AggregatedPredictions : null;
  const unsortedDataPoints = isActual ? actualData?.fog_actuals : predictionData?.predictions;
  const segmentHorizon = !isActual ? predictionData?.horizon : null;

  // Sort predictions first by horizon, then by datetime
  const dataPoints = isActual 
    ? unsortedDataPoints 
    : [...(unsortedDataPoints || [])].sort((a, b) => {
        const predA = a as FogPrediction;
        const predB = b as FogPrediction;
        // First sort by horizon
        const horizonCompare = (predA.horizon || 0) - (predB.horizon || 0);
        if (horizonCompare !== 0) return horizonCompare;
        // If horizons are equal, sort by datetime
        return new Date(predA.predicted_at).getTime() - new Date(predB.predicted_at).getTime();
      });

  const renderDataPoint = (point: FogActual | FogPrediction) => {
    if (isActual) {
      const actualPoint = point as FogActual;
      return (
        <>
          <TableCell>{new Date(actualPoint.forecast_time).toISOString()}</TableCell>
          <TableCell>{actualPoint.visibility_level}m</TableCell>
        </>
      );
    } else {
      const predPoint = point as FogPrediction;
      const matchesHorizon = predPoint.horizon === segmentHorizon;
      const rowStyle = matchesHorizon ? { backgroundColor: 'rgba(25, 118, 210, 0.08)' } : {};
      
      return (
        <>
          <TableCell sx={rowStyle}>{new Date(predPoint.predicted_at).toISOString()}</TableCell>
          <TableCell sx={rowStyle}>{predPoint.visibility_threshold}m</TableCell>
          <TableCell sx={rowStyle}>{predPoint.fog_prediction_confidence?.toFixed(2) ?? 'N/A'}</TableCell>
          <TableCell sx={rowStyle}>{predPoint.prediction_threshold?.toFixed(2) ?? 'N/A'}</TableCell>
          <TableCell sx={rowStyle}>
            {predPoint.horizon}min
            {matchesHorizon && (
              <Typography component="span" sx={{ ml: 1, color: 'primary.main', fontSize: '0.75rem' }}>
                (matches segment)
              </Typography>
            )}
          </TableCell>
        </>
      );
    }
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{new Date(data.segment_time).toISOString()}</TableCell>
        <TableCell>
          {isActual 
            ? (data.fog_predicted ? <Typography color="error">Yes</Typography> : 'No') 
            : `${(data as AggregatedPredictions).horizon}min`}
        </TableCell>
        <TableCell>
          {isActual 
            ? data.lowest_visibility_level ?? 'N/A' 
            : (data.fog_predicted ? <Typography color="error">Yes</Typography> : 'No')}
        </TableCell>
        <TableCell>
          {!isActual ? data.lowest_visibility_level ?? 'N/A' : ''}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Raw Data Points
              </Typography>
              {!isActual && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Predictions matching the segment horizon ({segmentHorizon}min) are highlighted
                </Typography>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Visibility</TableCell>
                    {!isActual && (
                      <>
                        <TableCell>Confidence</TableCell>
                        <TableCell>Threshold</TableCell>
                        <TableCell>Horizon</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataPoints?.map((point, index) => (
                    <TableRow key={index}>
                      {renderDataPoint(point)}
                    </TableRow>
                  ))}
                  {(!dataPoints || dataPoints.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={isActual ? 2 : 5} align="center">
                        No raw data points available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// Remove internal fetching and take data as props
interface FogForecastDebugProps {
  forecastLocations?: {
    locationId: number;
    locationName: string;
    forecast: FogForecastResponse;
  }[];
}

export default function FogForecastDebug({ forecastLocations }: FogForecastDebugProps) {
  const [forecasts, setForecasts] = useState<LocationForecast[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (forecastLocations && forecastLocations.length > 0) {
      const mappedForecasts = forecastLocations.map(location => ({
        locationId: location.locationId as NZAirports,
        locationName: location.locationName,
        forecast: location.forecast,
        error: null
      }));
      
      setForecasts(mappedForecasts);
      setLoading(false);
    } else {
      // Fall back to fetching data if no forecast locations are provided
      const fetchForecasts = async () => {
        setLoading(true);
        
        const newForecasts = await Promise.all(
          TARGET_AIRPORTS.map(async (locationId) => {
            try {
              const forecast = await fogForecastService.getFogForecast(locationId);
              const icaoCode = NZAirports[locationId] as string;
              return {
                locationId,
                locationName: AIRPORT_NAMES[icaoCode] || icaoCode,
                forecast,
                error: null
              };
            } catch (err) {
              console.error(`Error fetching forecast for ${locationId}:`, err);
              const icaoCode = NZAirports[locationId] as string;
              return {
                locationId,
                locationName: AIRPORT_NAMES[icaoCode] || icaoCode,
                forecast: null,
                error: `Failed to fetch forecast: ${err}`
              };
            }
          })
        );

        setForecasts(newForecasts);
        setLoading(false);
      };

      fetchForecasts();

      // Refresh every 15 minutes
      const intervalId = setInterval(fetchForecasts, 15 * 60 * 1000);
      return () => clearInterval(intervalId);
    }
  }, [forecastLocations]);

  if (loading && forecasts.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading forecasts...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Typography variant="h4" gutterBottom>Fog Forecast Debug View</Typography>

      {forecasts.map((locationForecast) => (
        <Paper key={locationForecast.locationId} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h5" gutterBottom>
            {locationForecast.locationName} ({NZAirports[locationForecast.locationId]})
          </Typography>

          {locationForecast.error ? (
            <Typography color="error">{locationForecast.error}</Typography>
          ) : locationForecast.forecast ? (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Requested Time: {new Date(locationForecast.forecast.requested_time).toISOString()}
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Actuals:</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: '50px' }} />
                      <TableCell>Time Segment</TableCell>
                      <TableCell>Fog Present</TableCell>
                      <TableCell>Median Visibility</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {locationForecast.forecast.aggregated_actuals.map((actual, index) => (
                      <DataRow key={index} data={actual} type="actual" />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Predictions:</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: '50px' }} />
                      <TableCell>Time Segment</TableCell>
                      <TableCell>Horizon</TableCell>
                      <TableCell>Fog Predicted</TableCell>
                      <TableCell>Lowest Visibility</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {locationForecast.forecast.aggregated_predictions.map((prediction, index) => (
                      <DataRow key={index} data={prediction} type="prediction" />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Typography>No forecast data available</Typography>
          )}
        </Paper>
      ))}
    </Box>
  );
} 